import { useIntl } from 'react-intl'
import FormList from '../FormList/FormList'
import { AttachmentProps } from '@/app/models/attachment'
import InputEmails from '../../UI/InputEmails/InputEmails'
import AddAttachment from '../../UI/AddAttachment/AddAttachment'
import ArticleAbstract from '../ArticleAbstract/ArticleAbstract'
import { LuxInputSwitch, LuxSelect, LuxTextInput } from '@lux/wardrobe-lib'
import { FormProps, ResourceProps, ResourceType } from '@/app/models/new-resource'
import { useEffect, useState } from 'react'
import './Information.scss'

const Information = (props: {
    isEdit: boolean
    resourceType: ResourceType
    folderDetails: ResourceProps
    referenceFolderObject: ResourceProps
    departmentList: { value: string; label: string }[]
    folderTitle: string
    setFolderTitle: (folderTitle: string) => void
    folderType: string
    setFolderType: (folderType: string) => void
    department: string
    setDepartment: (department: string) => void
    recipients: string[]
    setRecipients: (recipients: string[]) => void
    articleTitle: string
    setArticleTitle: (articleTitle: string) => void
    formTitle: string
    setFormTitle: (formTitle: string) => void
    selectedForms: FormProps[]
    setSelectedForms: (selectedForms: FormProps[]) => void
    abstract: any
    setAbstract: (abstract: any) => void
    attachments: AttachmentProps[]
    setAttachments: (attachments: AttachmentProps[]) => void
    resetViewsCount: boolean
    setResetViewsCount: (resetViewsCount: boolean) => void
    requiredConfirmReading: boolean
    setRequiredConfirmReading: (requiredConfirmReading: boolean) => void
    enableCommentsAndReactions: boolean
    setEnableCommentsAndReactions: (enableCommentsAndReactions: boolean) => void
    showResetViewsCount: boolean
    setShowResetViewsCount: (showResetViewsCount: boolean) => void
}) => {
    const {
        isEdit,
        resourceType,
        folderDetails,
        departmentList,
        referenceFolderObject,
        folderTitle,
        setFolderTitle,
        folderType,
        setFolderType,
        department,
        setDepartment,
        recipients,
        setRecipients,
        articleTitle,
        setArticleTitle,
        formTitle,
        setFormTitle,
        selectedForms,
        setSelectedForms,
        abstract,
        setAbstract,
        attachments,
        setAttachments,
        resetViewsCount,
        setResetViewsCount,
        requiredConfirmReading,
        setRequiredConfirmReading,
        enableCommentsAndReactions,
        setEnableCommentsAndReactions,
        showResetViewsCount,
        setShowResetViewsCount,
    } = props

    const intl = useIntl()
    const [form, onSetForm] = useState<any>({ attachmentList: { value: [] } })
    const [banner, setBanner] = useState<string[]>(referenceFolderObject?.bannerList || [])
    const [country, setCountry] = useState<string[]>(referenceFolderObject?.countryList || [])
    const [region, setRegion] = useState<string[]>(referenceFolderObject?.regionList || [])

    const componentClass = 'smart-modals-new-resource-information'

    const onUpload = (files) => {
        setAttachments([
            ...(isEdit ? [] : attachments),
            ...files.map((file: any) => ({
                _id: file.id,
                file: file.file,
                fileUrl: file.url,
                fileName: file.file.name,
                fileExtension: file.file.type,
            })),
        ])
    }

    useEffect(() => {
        onSetForm({
            ...form,
            attachmentList: {
                value: [
                    ...attachments.map((file: any) => ({
                        fileBlob: file.file,
                        file: file.file,
                        url: file.url,
                        _id: file.id,
                        fileExtension: file.fileExtension,
                        fileName: file.fileName,
                    })),
                ],
            },
        })
    }, [attachments])

    const onRemoveAttachment = (file) => {
        let newList = [...attachments]
        const fileIndex = newList.findIndex((i) => i.file.name === file.name)
        newList.splice(fileIndex, 1)
        setAttachments(
            newList.map((file) => ({
                _id: file.id || file._id,
                file: file.file,
                fileUrl: file.url,
                fileName: file.file.name,
                fileExtension: file.file.type,
            }))
        )
    }

    useEffect(() => {
        if (
            referenceFolderObject &&
            referenceFolderObject.bannerList &&
            referenceFolderObject.regionList &&
            referenceFolderObject.countryList
        ) {
            setBanner(referenceFolderObject.bannerList)
            setRegion(referenceFolderObject.regionList)
            setCountry(referenceFolderObject.countryList)
        }
    }, [referenceFolderObject])

    useEffect(() => {
        onSetForm({
            ...form,
            attachmentList: {
                value: attachments,
            },
        })
    }, [attachments])

    return (
        <div
            className={`${componentClass}${
                resourceType === ResourceType.article || resourceType === ResourceType.form
                    ? ' flex-wrap'
                    : ''
            }`}>
            {(resourceType === ResourceType.folder || resourceType === ResourceType.subfolder) && (
                <>
                    <div className={`${componentClass}__folderTitle`}>
                        <LuxTextInput
                            icon="pencil"
                            mandatory={true}
                            value={folderTitle}
                            label={intl.formatMessage({
                                id: `modals.new_resource.label.${resourceType}_title`,
                                defaultMessage: `${intl.formatMessage({
                                    id: `modals.new_resource.folder_type.${resourceType}`,
                                    defaultMessage: resourceType,
                                })} Title`,
                            })}
                            iconPosition="right"
                            placeholder={intl.formatMessage({
                                id: 'modals.new_resource.placeholder.type_here',
                                defaultMessage: 'Type here...',
                            })}
                            onChangeCallback={(e: any) => setFolderTitle(e)}
                        />
                    </div>
                </>
            )}
            {resourceType === ResourceType.article && (
                <div className={`${componentClass}__articleTitle`}>
                    <LuxTextInput
                        icon="pencil"
                        mandatory={true}
                        value={articleTitle}
                        label={intl.formatMessage({
                            id: `modals.new_resource.label.${resourceType}_title`,
                            defaultMessage: `${intl.formatMessage({
                                id: `modals.new_resource.folder_type.${resourceType}`,
                                defaultMessage: resourceType,
                            })} Title`,
                        })}
                        iconPosition="right"
                        placeholder={intl.formatMessage({
                            id: 'modals.new_resource.placeholder.type_here',
                            defaultMessage: 'Type here...',
                        })}
                        onChangeCallback={(e: any) => setArticleTitle(e)}
                    />
                </div>
            )}
            {resourceType === ResourceType.form && (
                <>
                    <div className={`${componentClass}__formTitle`}>
                        <LuxTextInput
                            icon="pencil"
                            mandatory={true}
                            value={formTitle}
                            label={intl.formatMessage({
                                id: `modals.new_resource.label.${resourceType}_title`,
                                defaultMessage: `${intl.formatMessage({
                                    id: `modals.new_resource.folder_type.${resourceType}`,
                                    defaultMessage: resourceType,
                                })} Title`,
                            })}
                            iconPosition="right"
                            placeholder={intl.formatMessage({
                                id: 'modals.new_resource.placeholder.type_here',
                                defaultMessage: 'Type here...',
                            })}
                            onChangeCallback={(e: any) => setFormTitle(e)}
                        />
                    </div>
                    <div className={`${componentClass}__recipient`}>
                        <label>
                            <span className="mandatory">*</span>
                            <span className="label">
                                {intl.formatMessage({
                                    id: 'modals.news_execution.project_specs.details.recipient_emails',
                                    defaultMessage: 'Recipient Emails',
                                })}
                            </span>
                        </label>
                        <InputEmails
                            value={recipients}
                            id={componentClass}
                            setValue={setRecipients}
                            placeholder={intl.formatMessage({
                                id: 'modals.new_resource.placeholder.type_here_and_hit_enter',
                                defaultMessage: 'Type here and hit enter...',
                            })}
                        />
                    </div>
                </>
            )}
            {(resourceType === ResourceType.file ||
                resourceType === ResourceType.article ||
                resourceType === ResourceType.form) && (
                <div className={`${componentClass}__department`}>
                    <LuxSelect
                        mandatory={true}
                        value={department}
                        onChangeCallback={(e: any) => setDepartment(e)}
                        label={intl.formatMessage({
                            id: 'modals.news_execution.project_specs.details.department',
                            defaultMessage: 'Department',
                        })}
                        options={departmentList.map((department) => ({
                            ...department,
                            label: intl.formatMessage({
                                id: department.label,
                                defaultMessage: department.label.split('.').pop(),
                            }),
                        }))}
                        placeholder={intl.formatMessage({
                            id: 'modals.new.activity.section.main.info.select.department',
                            defaultMessage: 'Select department',
                        })}
                    />
                </div>
            )}
            {resourceType === ResourceType.file && (
                <div className={`${componentClass}__selectFile`}>
                    <label>
                        <span className="mandatory">*</span>
                        <span className="label">
                            {intl.formatMessage({
                                id: 'modals.new_resource.label.select_file',
                                defaultMessage: 'Select File',
                            })}
                        </span>
                    </label>
                    {!form.attachmentList?.value?.length && (
                        <>
                            <div className={`${componentClass}__selectFile__noUploadedFiles`}>
                                {intl.formatMessage({
                                    id: 'modals.new_resource.label.no_uploaded_files',
                                    defaultMessage: 'There are no uploaded files yet.',
                                })}
                            </div>
                        </>
                    )}
                    <AddAttachment
                        form={form}
                        showRemoveIcon={!isEdit}
                        showSplitFile={false}
                        customEndpoint="resource"
                        onRemove={(file) => onRemoveAttachment(file)}
                        onUpload={(fileList: any) => onUpload(fileList)}
                    />
                </div>
            )}
            {resourceType === ResourceType.article && (
                <>
                    <div className={`${componentClass}__abstract`}>
                        <ArticleAbstract
                            abstract={abstract}
                            setAbstract={setAbstract}
                            attachments={attachments}
                            setAttachments={setAttachments}
                        />
                    </div>
                    <div className={`${componentClass}__requiredConfirmReading`}>
                        <LuxInputSwitch
                            value={requiredConfirmReading}
                            additionalProps={{ disabled: false }}
                            onChangeCallback={(value: boolean) => setRequiredConfirmReading(value)}
                            label={`${intl.formatMessage({
                                id: 'modals.new_resource.label.requiredConfirmReading',
                                defaultMessage: 'Confirm reading',
                            })} (${requiredConfirmReading ? 'ON' : 'OFF'})`}
                        />
                    </div>
                    <div className={`${componentClass}__enableCommentsAndReactions`}>
                        <LuxInputSwitch
                            value={enableCommentsAndReactions}
                            additionalProps={{ disabled: false }}
                            onChangeCallback={(value: boolean) =>
                                setEnableCommentsAndReactions(value)
                            }
                            label={`${intl.formatMessage({
                                id: 'modals.new_resource.label.enableCommentsAndReactions',
                                defaultMessage: 'Enable comments and reactions',
                            })} (${enableCommentsAndReactions ? 'ON' : 'OFF'})`}
                        />
                    </div>
                    {isEdit && showResetViewsCount && (
                        <div className={`${componentClass}__resetViewsCount`}>
                            <LuxInputSwitch
                                value={resetViewsCount}
                                additionalProps={{ disabled: false }}
                                onChangeCallback={(value: boolean) => setResetViewsCount(value)}
                                title={intl.formatMessage({
                                    id: 'modals.new_resource.label.resetViewsCount',
                                    defaultMessage: 'Reset Count',
                                })}
                                label={`${intl.formatMessage({
                                    id: 'modals.new_resource.label.resetViewsCount',
                                    defaultMessage: 'Reset Count',
                                })} (${resetViewsCount ? 'ON' : 'OFF'})`}
                            />
                        </div>
                    )}
                </>
            )}
            {resourceType === ResourceType.form && (
                <div className={`${componentClass}__formList`}>
                    <FormList
                        referenceObjectFolder={referenceFolderObject}
                        selectedForms={selectedForms}
                        setSelectedForms={setSelectedForms}
                        banner={banner}
                        country={country}
                        region={region}
                    />
                </div>
            )}
        </div>
    )
}

export default Information
